import { AutocompleteFilter } from "./AutocompleteFilter";
import {
  booleanToReadableString,
  dateRangeToReadableString,
  entitiesToReadableString,
  numericalRangeToReadableString,
} from "portal/components/filters/filter";
import { Button, SvgIcon, Typography } from "@mui/material";
import { CarbonCropFilter } from "./CarbonCropFilter";
import { CarbonDateTimeRangePicker } from "portal/components/CarbonDateTimeRangePicker";
import { classes } from "portal/theme/theme";
import {
  FeatureFlag,
  useFeatureFlagForCustomer,
} from "portal/utils/hooks/useFeatureFlag";
import {
  FilterChip,
  FilterChipProps,
} from "portal/components/filters/FilterChip";
import { getDateRangeShortcuts } from "portal/utils/reports";
import { getRadiusFilterUnits } from "portal/utils/categoryCollection/radius";
import { isEqual } from "portal/utils/equality";
import { keys, pick } from "common/utils/objects";
import { NumericalRange } from "../../filters/NumericalRange";
import { OptionalBooleanSelect } from "portal/components/filters/OptionalBoolean";
import { PlantProfileCategoryFilter } from "./PlantProfileCategoryFilter";
import { TFunction } from "i18next";
import { titleCase } from "portal/utils/strings";
import {
  UiCropFilterValue,
  UiPredictionPointBaseFilters,
  UiPredictionPointFilters,
} from "portal/utils/categoryCollection/predictionPoints";
import { UploadedByOperatorIcon } from "../imageOverlays/IconOverlay";
import { useGetRobotFilterOptions } from "../useGetRobotFilterOptions";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import { VeselkaCropFilter } from "./VeselkaCropFilter";
import ModelIcon from "portal/images/icons/model.svg?react";
import React, { FC, useEffect, useMemo, useState } from "react";

const predictionPointEditableFilterKeys: readonly (keyof UiPredictionPointBaseFilters)[] =
  Object.freeze(
    keys({
      crops: true,
      robots: true,
      capturedAt: true,
      radiusUserUnits: true,
      uploadedByOperator: true,
      plantProfileCategoryId: true,
    } satisfies {
      [K in keyof UiPredictionPointBaseFilters]: true;
    })
  );

export const INITIAL_PREDICTION_POINT_FILTERS: UiPredictionPointFilters = {
  crops: [],
  robots: [],
  capturedAt: undefined,
  radiusUserUnits: undefined,
  sessionId: undefined,
  categoryIds: [],
  uploadedByOperator: undefined,
  plantProfileCategoryId: undefined,
};

const getDisplayNames = (
  t: TFunction
): Record<keyof UiPredictionPointBaseFilters, string> => ({
  crops: t("models.crops.crop_other"),
  robots: t("models.robots.robot_other"),
  capturedAt: t("components.categoryCollectionProfile.filters.capturedAt"),
  radiusUserUnits: t("components.categoryCollectionProfile.filters.radius"),
  uploadedByOperator: t(
    "components.categoryCollectionProfile.filters.uploadedByOperator"
  ),
  plantProfileCategoryId: t(
    "components.categoryCollectionProfile.filters.sourceCategory"
  ),
});

interface PredictionPointFiltersProps {
  scopeCustomerUuid?: string; // scopes robot and veselka image filters to this customer.
  serial?: string; // scopes the crop list to this robot when set (robot view).
  filters: UiPredictionPointFilters;
  onChange: (filters: UiPredictionPointFilters) => void;
  disabled?: boolean;
  hasSession?: boolean;
  showPlantProfileCategoryFilter?: boolean;
}

export const PredictionPointFilters: FC<PredictionPointFiltersProps> = ({
  scopeCustomerUuid,
  serial,
  filters,
  onChange,
  disabled = false,
  hasSession = false,
  showPlantProfileCategoryFilter = false,
}) => {
  const { t } = useTranslation();
  const { measurementSystem } = useSelf();
  const radiusFilterUnits = getRadiusFilterUnits(measurementSystem);

  const [localFilters, setLocalFilters] =
    useState<UiPredictionPointFilters>(filters);

  const {
    isEnabled: hasCustomCategories,
    isLoading: isCustomCategoriesLoading,
  } = useFeatureFlagForCustomer(
    FeatureFlag.CUSTOM_CATEGORIES,
    scopeCustomerUuid
  );
  const useCarbonCrops =
    Boolean(hasCustomCategories) && !isCustomCategoriesLoading;

  useEffect(() => {
    setLocalFilters((previous) =>
      previous.crops.length === 0 ? previous : { ...previous, crops: [] }
    );
  }, [useCarbonCrops]);

  // robots
  const {
    robots,
    isLoading: isRobotsLoading,
    error: robotsError,
  } = useGetRobotFilterOptions(scopeCustomerUuid);

  const hasActiveFilters = useMemo(() => {
    const local = pick(localFilters, predictionPointEditableFilterKeys);
    const initial = pick(
      INITIAL_PREDICTION_POINT_FILTERS,
      predictionPointEditableFilterKeys
    );
    return !isEqual(local, initial);
  }, [localFilters]);

  const touched = useMemo(() => {
    const local = pick(localFilters, predictionPointEditableFilterKeys);
    const committed = pick(filters, predictionPointEditableFilterKeys);
    return !isEqual(local, committed);
  }, [localFilters, filters]);

  const displayNames = getDisplayNames(t);
  const classNames = "md:max-w-1/3";
  const labelClassName = "p-0 m-0 text-xs";
  const onCropFilterChange = (crops: UiCropFilterValue[]): void => {
    setLocalFilters((previous) => ({ ...previous, crops }));
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-6 flex-wrap items-end">
        {showPlantProfileCategoryFilter && (
          <div className={classNames}>
            <PlantProfileCategoryFilter
              scopeCustomerUuid={scopeCustomerUuid}
              value={localFilters.plantProfileCategoryId}
              disabled={disabled}
              onChange={(plantProfileCategoryId) =>
                setLocalFilters({
                  ...localFilters,
                  plantProfileCategoryId,
                })
              }
            />
          </div>
        )}
        <div className={classNames}>
          {useCarbonCrops ? (
            <CarbonCropFilter
              customerUuid={scopeCustomerUuid}
              label={titleCase(displayNames.crops)}
              value={localFilters.crops}
              onChange={onCropFilterChange}
            />
          ) : (
            <VeselkaCropFilter
              serial={serial}
              label={titleCase(displayNames.crops)}
              value={localFilters.crops}
              onChange={onCropFilterChange}
            />
          )}
        </div>
        <div className={classNames}>
          <AutocompleteFilter
            loading={isRobotsLoading}
            loadingError={Boolean(robotsError)}
            label={titleCase(displayNames.robots)}
            options={robots || []}
            value={localFilters.robots}
            onChange={(value) =>
              setLocalFilters({ ...localFilters, robots: value })
            }
          />
        </div>
        <div className={classNames}>
          <p className={labelClassName}>{displayNames.radiusUserUnits}</p>
          <NumericalRange
            className="max-w-60"
            value={localFilters.radiusUserUnits ?? [undefined, undefined]}
            min={0}
            onChange={(radiusUserUnits) => {
              setLocalFilters({ ...localFilters, radiusUserUnits });
            }}
            units={radiusFilterUnits}
          />
        </div>
        <div>
          <p className={classes(labelClassName, "pb-2")}>
            {displayNames.capturedAt}
          </p>
          <CarbonDateTimeRangePicker
            className="gap-0"
            calendars={1}
            openEnded
            disableFuture
            // eslint-disable-next-line unicorn/no-null -- null here specifies that the component is controlled vs undefined which means uncontrolled
            value={localFilters.capturedAt ?? [null, null]}
            slotProps={{
              shortcuts: { items: getDateRangeShortcuts(t) },
              textField: {
                size: "small",
                className: "m-0",
              },
              actionBar: {
                actions: ["clear", "cancel", "accept"],
              },
            }}
            onChange={(newRange) => {
              setLocalFilters({ ...localFilters, capturedAt: newRange });
            }}
          />
        </div>
        <div className="max-w-40">
          <p className={classes(labelClassName, "pb-2")}>
            {titleCase(displayNames.uploadedByOperator)}
          </p>
          <OptionalBooleanSelect
            value={localFilters.uploadedByOperator}
            onChange={(value) => {
              setLocalFilters({ ...localFilters, uploadedByOperator: value });
            }}
            trueText={t(
              "components.categoryCollectionProfile.filters.uploaded"
            )}
            falseText={t(
              "components.categoryCollectionProfile.filters.notUploaded"
            )}
            trueIcon={<UploadedByOperatorIcon />}
          />
        </div>
      </div>
      <div className="flex w-full justify-end items-center gap-4">
        {touched && (
          <Typography className="text-xs" color="textSecondary">
            {t("components.categoryCollectionProfile.filters.unappliedFilters")}
          </Typography>
        )}
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={() => {
            setLocalFilters(INITIAL_PREDICTION_POINT_FILTERS);
          }}
          disabled={disabled || !hasActiveFilters}
        >
          {t("utils.actions.clear")}
        </Button>
        <Button
          size="small"
          variant="contained"
          className="h-8 whitespace-nowrap bg-white text-black hover:bg-zinc-100 active:bg-zinc-200 disabled:bg-zinc-500 disabled:text-lighten-500"
          onClick={() => {
            onChange(localFilters);
          }}
          disabled={disabled || !touched}
          startIcon={
            hasSession || filters.sessionId ? (
              <SvgIcon className="text-sm">
                <ModelIcon />
              </SvgIcon>
            ) : undefined
          }
        >
          {t("utils.actions.apply")}
        </Button>
      </div>
    </div>
  );
};

interface PredictionPointFilterChipSummaryProps {
  filters: UiPredictionPointBaseFilters;
}
export const PredictionPointFilterChipSummary: FC<
  PredictionPointFilterChipSummaryProps
> = ({ filters }) => {
  const { t, i18n } = useTranslation();
  const displayNames = getDisplayNames(t);

  const chipMap: Record<keyof UiPredictionPointBaseFilters, FilterChipProps> =
    useMemo(
      () => ({
        plantProfileCategoryId: {
          name: displayNames.plantProfileCategoryId,
          value: filters.plantProfileCategoryId?.name ?? "",
        },
        crops: {
          name: displayNames.crops,
          value: entitiesToReadableString(filters.crops),
        },
        robots: {
          name: displayNames.robots,
          value: entitiesToReadableString(filters.robots),
        },
        capturedAt: {
          name: displayNames.capturedAt,
          value: dateRangeToReadableString(t, i18n, filters.capturedAt),
        },
        radiusUserUnits: {
          name: displayNames.radiusUserUnits,
          value: numericalRangeToReadableString(t, filters.radiusUserUnits),
        },
        uploadedByOperator: {
          name: displayNames.uploadedByOperator,
          value: booleanToReadableString(t, filters.uploadedByOperator),
        },
      }),
      [
        displayNames.capturedAt,
        displayNames.crops,
        displayNames.radiusUserUnits,
        displayNames.robots,
        displayNames.plantProfileCategoryId,
        displayNames.uploadedByOperator,
        filters.capturedAt,
        filters.crops,
        filters.radiusUserUnits,
        filters.robots,
        filters.plantProfileCategoryId,
        filters.uploadedByOperator,
        i18n,
        t,
      ]
    );

  const chips: FilterChipProps[] = useMemo(() => {
    return Object.values(chipMap).filter(({ value }) => value !== "");
  }, [chipMap]);

  return chips.map(({ name, value }) => (
    <FilterChip key={name} name={name} value={value} />
  ));
};
